import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
    Target,
    TrendingUp,
    CheckCircle2,
    AlertTriangle,
    ChevronDown,
    ChevronRight,
    Link2,
    ExternalLink,
    Loader2,
} from 'lucide-react';
import clsx from 'clsx';

export default function AlignmentPage() {
    const { t } = useTranslation();
    usePageTitle('nav.alignment');
    const { workspace } = useAuthStore();
    const navigate = useNavigate();
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [expandedObjectives, setExpandedObjectives] = useState<Set<string>>(new Set());
    const [expandedKRs, setExpandedKRs] = useState<Set<string>>(new Set());

    const { data, isLoading } = useQuery({
        queryKey: ['alignment-tree', workspace?.id, selectedYear],
        queryFn: () => api.getAlignmentTree(workspace?.id || '', selectedYear),
        enabled: !!workspace?.id,
    });

    const toggleObjective = (id: string) => {
        const newSet = new Set(expandedObjectives);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setExpandedObjectives(newSet);
    };

    const toggleKR = (id: string) => {
        const newSet = new Set(expandedKRs);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setExpandedKRs(newSet);
    };

    const summary = data?.data?.summary;
    const alignmentTree = data?.data?.alignment_tree || [];
    const orphanTasks = data?.data?.orphan_tasks || [];

    const getScoreColor = (score: number) => {
        if (score >= 80) return 'text-success-600 bg-success-100 dark:bg-success-600/20';
        if (score >= 50) return 'text-warning-600 bg-warning-100 dark:bg-warning-600/20';
        return 'text-danger-600 bg-danger-100 dark:bg-danger-600/20';
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'on_track':
            case 'completed':
                return 'text-success-600';
            case 'at_risk':
                return 'text-warning-600';
            case 'off_track':
                return 'text-danger-600';
            default:
                return 'text-neutral-600';
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <Link2 className="w-7 h-7 text-primary-600" />
                    {t('alignment.title', 'Alignment')}
                </h1>
                <p className="text-neutral-500 mt-1">
                    {t('alignment.subtitle', 'Xem cây liên kết mục tiêu và đo lường mức độ alignment')}
                </p>
            </div>

            {/* Alignment Score Card */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className={clsx(
                    'p-4 rounded-xl',
                    getScoreColor(summary?.alignment_score || 0)
                )}>
                    <div className="text-sm font-medium opacity-80">
                        {t('alignment.score', 'Alignment Score')}
                    </div>
                    <div className="text-3xl font-bold mt-1">
                        {summary?.alignment_score || 0}%
                    </div>
                    <div className="text-xs mt-1 opacity-70">
                        {summary?.linked_tasks || 0} / {summary?.total_active_tasks || 0} {t('alignment.tasksLinked', 'tasks liên kết')}
                    </div>
                </div>

                <div className="bg-white dark:bg-neutral-800 p-4 rounded-xl border border-neutral-200 dark:border-neutral-700">
                    <div className="text-sm font-medium text-neutral-500">
                        {t('alignment.objectives', 'Mục tiêu')}
                    </div>
                    <div className="text-2xl font-bold mt-1">
                        {summary?.objectives_count || 0}
                    </div>
                </div>

                <div className="bg-white dark:bg-neutral-800 p-4 rounded-xl border border-neutral-200 dark:border-neutral-700">
                    <div className="text-sm font-medium text-neutral-500">
                        {t('alignment.krsWithoutTasks', 'KR chưa có task')}
                    </div>
                    <div className="text-2xl font-bold mt-1 text-warning-600">
                        {summary?.krs_without_tasks || 0}
                    </div>
                </div>

                <div className="bg-white dark:bg-neutral-800 p-4 rounded-xl border border-neutral-200 dark:border-neutral-700">
                    <div className="text-sm font-medium text-neutral-500">
                        {t('alignment.orphanTasks', 'Task mồ côi')}
                    </div>
                    <div className="text-2xl font-bold mt-1 text-danger-600">
                        {summary?.orphan_task_count || 0}
                    </div>
                </div>
            </div>

            {/* Year selector */}
            <div className="flex gap-2 mb-6">
                {[currentYear - 1, currentYear, currentYear + 1].map((year) => (
                    <button
                        key={year}
                        onClick={() => setSelectedYear(year)}
                        className={clsx(
                            'px-4 py-2 rounded-lg font-medium transition-colors',
                            selectedYear === year
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                        )}
                    >
                        {year}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Alignment Tree */}
                <div className="lg:col-span-2 space-y-3">
                    <h2 className="text-lg font-semibold mb-3">
                        {t('alignment.tree', 'Cây liên kết')}
                    </h2>

                    {alignmentTree.length === 0 ? (
                        <div className="bg-neutral-50 dark:bg-neutral-800 p-8 text-center rounded-lg">
                            <Target className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
                            <p className="text-neutral-500">
                                {t('alignment.noObjectives', 'Chưa có mục tiêu nào. Tạo mục tiêu để bắt đầu!')}
                            </p>
                        </div>
                    ) : (
                        alignmentTree.map((item: any) => (
                            <div key={item.objective.id} className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                                {/* Objective row */}
                                <button
                                    onClick={() => toggleObjective(item.objective.id)}
                                    className="w-full flex items-center gap-3 p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 text-left"
                                >
                                    {expandedObjectives.has(item.objective.id) ? (
                                        <ChevronDown className="w-5 h-5 text-neutral-400" />
                                    ) : (
                                        <ChevronRight className="w-5 h-5 text-neutral-400" />
                                    )}
                                    <Target className={clsx('w-5 h-5', getStatusColor(item.objective.status))} />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate">{item.objective.title}</div>
                                        <div className="text-xs text-neutral-500">
                                            {item.stats.kr_count} KR • {item.stats.total_linked_tasks} tasks
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-20 h-2 bg-neutral-200 dark:bg-neutral-600 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-primary-600 rounded-full transition-all"
                                                style={{ width: `${item.objective.progress}%` }}
                                            />
                                        </div>
                                        <span className="text-sm font-medium w-10 text-right">
                                            {Math.round(item.objective.progress)}%
                                        </span>
                                    </div>
                                </button>

                                {/* Key Results */}
                                {expandedObjectives.has(item.objective.id) && (
                                    <div className="border-t border-neutral-200 dark:border-neutral-700">
                                        {item.key_results.length === 0 ? (
                                            <div className="p-4 pl-12 text-sm text-neutral-500 italic">
                                                {t('alignment.noKRs', 'Chưa có Key Result nào')}
                                            </div>
                                        ) : (
                                            item.key_results.map((kr: any) => (
                                                <div key={kr.id}>
                                                    <button
                                                        onClick={() => toggleKR(kr.id)}
                                                        className="w-full flex items-center gap-3 p-3 pl-12 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 text-left"
                                                    >
                                                        {expandedKRs.has(kr.id) ? (
                                                            <ChevronDown className="w-4 h-4 text-neutral-400" />
                                                        ) : (
                                                            <ChevronRight className="w-4 h-4 text-neutral-400" />
                                                        )}
                                                        <TrendingUp className={clsx('w-4 h-4', getStatusColor(kr.status))} />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-sm truncate">{kr.title}</div>
                                                            <div className="text-xs text-neutral-500">
                                                                {kr.task_stats.completed}/{kr.task_stats.total} tasks
                                                                {kr.task_stats.total === 0 && (
                                                                    <span className="ml-2 text-warning-600">
                                                                        ⚠️ {t('alignment.noLinkedTasks', 'Chưa liên kết task')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-16 h-1.5 bg-neutral-200 dark:bg-neutral-600 rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full bg-secondary-600 rounded-full"
                                                                    style={{ width: `${kr.progress}%` }}
                                                                />
                                                            </div>
                                                            <span className="text-xs w-8 text-right">
                                                                {Math.round(kr.progress)}%
                                                            </span>
                                                        </div>
                                                    </button>

                                                    {/* Linked Tasks */}
                                                    {expandedKRs.has(kr.id) && kr.linked_tasks.length > 0 && (
                                                        <div className="bg-neutral-50 dark:bg-neutral-900/50 border-t border-neutral-100 dark:border-neutral-700">
                                                            {kr.linked_tasks.map((task: any) => (
                                                                <div
                                                                    key={task.id}
                                                                    className="flex items-center gap-2 p-2 pl-20 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700/30"
                                                                >
                                                                    {task.completed_at ? (
                                                                        <CheckCircle2 className="w-4 h-4 text-success-600" />
                                                                    ) : (
                                                                        <div className="w-4 h-4 rounded-full border-2 border-neutral-300" />
                                                                    )}
                                                                    <span className={clsx(
                                                                        'flex-1 truncate',
                                                                        task.completed_at && 'line-through text-neutral-400'
                                                                    )}>
                                                                        {task.title}
                                                                    </span>
                                                                    <span className="text-xs text-neutral-400">
                                                                        {task.board_name}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Orphan Tasks Sidebar */}
                <div>
                    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
                        <h3 className="font-semibold flex items-center gap-2 mb-3">
                            <AlertTriangle className="w-4 h-4 text-danger-600" />
                            {t('alignment.orphanTasksTitle', 'Task chưa liên kết')}
                        </h3>
                        <p className="text-xs text-neutral-500 mb-4">
                            {t('alignment.orphanTasksDesc', 'Các task này chưa được liên kết với bất kỳ Key Result nào. Hãy liên kết để đo lường đóng góp vào mục tiêu.')}
                        </p>

                        {orphanTasks.length === 0 ? (
                            <div className="text-center py-4">
                                <CheckCircle2 className="w-8 h-8 text-success-600 mx-auto mb-2" />
                                <p className="text-sm text-success-600 font-medium">
                                    {t('alignment.allAligned', 'Tất cả task đã liên kết!')}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                {orphanTasks.map((task: any) => (
                                    <div
                                        key={task.id}
                                        className="p-2 bg-neutral-50 dark:bg-neutral-700/50 rounded border border-neutral-200 dark:border-neutral-600"
                                    >
                                        <div className="flex items-start gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm truncate">{task.title}</div>
                                                <div className="text-xs text-neutral-500 flex items-center gap-1">
                                                    {task.board_name}
                                                    {task.due_date && (
                                                        <span className="text-danger-600">
                                                            • {new Date(task.due_date).toLocaleDateString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => navigate(`/boards/${task.board_id}`)}
                                                className="p-1 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded"
                                                title={t('alignment.goToBoard', 'Đến board')}
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
